import { Tooltip } from '@mantine/core';
import {
  Background,
  ControlButton,
  Controls,
  type Edge,
  Handle,
  type Node,
  type NodeMouseHandler,
  type NodeProps,
  Position,
  ReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  type KeyboardEvent,
  type MouseEvent,
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Icon } from '../../../ui-library/icon/icon';
import { ClickableLabel, FocusableCard, TurnToggle } from './unified-process-graph-chart.styles';
import {
  COMMENT_TOOLTIP_WIDTH,
  DOT_SIZE,
  HIDDEN_HANDLE_STYLE,
  LABEL_MAX_WIDTH,
  LANE_GROUPS,
  MONOSPACE_FONT,
  TURN_PREVIEW_LIMIT,
} from './unified-process-graph.constants';
import { buildFlowGraph, toAgentSessionId } from './unified-process-graph.helpers';
import { InteractiveNodeKind, TurnExpansion } from './unified-process-graph.type';
import type { CommentPreview, CommitPreview, UnifiedProcessGraphChartProps } from './unified-process-graph.type';

/**
 * Hover preview for a PR comment node: the first comment's author, file and
 * trimmed text, plus a count of any further comments. Rendered as plain text.
 *
 * @param {{ preview: CommentPreview }} props - The node's comment preview
 * @returns {JSX.Element} The tooltip body
 */
const CommentTooltipContent = ({ preview }: { preview: CommentPreview }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, lineHeight: 1.4 }}>
    <div style={{ fontWeight: 600 }}>
      {preview.author ?? 'Unknown author'}
      {preview.path && (
        <span style={{ fontWeight: 400, opacity: 0.75, fontFamily: MONOSPACE_FONT, fontSize: 10.5 }}>
          {' '}
          · {preview.path}
        </span>
      )}
    </div>
    <div style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{preview.body || 'No text'}</div>
    {preview.remaining > 0 && (
      <div style={{ opacity: 0.75, fontSize: 11 }}>
        +{preview.remaining} more comment{preview.remaining !== 1 ? 's' : ''}
      </div>
    )}
  </div>
);

/**
 * Hover preview for a commit node: short hash and author, then the full commit
 * message with its line breaks. Rendered as plain text.
 *
 * @param {{ preview: CommitPreview }} props - The node's commit preview
 * @returns {JSX.Element} The tooltip body
 */
const CommitTooltipContent = ({ preview }: { preview: CommitPreview }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, lineHeight: 1.4 }}>
    {(preview.hash || preview.author) && (
      <div style={{ fontWeight: 600 }}>
        {preview.hash && <span style={{ fontFamily: MONOSPACE_FONT, fontSize: 11 }}>{preview.hash}</span>}
        {preview.hash && preview.author && ' · '}
        {preview.author}
      </div>
    )}
    <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{preview.message || 'No message'}</div>
  </div>
);

/**
 * Card body for a commit node: the short hash over the message's first line,
 * each kept to one line and cut off with an ellipsis to fit the card.
 *
 * @param {{ preview: CommitPreview }} props - The node's commit preview
 * @returns {JSX.Element} The two card lines
 */
const CommitCardContent = ({ preview }: { preview: CommitPreview }) => {
  const lineStyle = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;
  return (
    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <span style={{ ...lineStyle, fontFamily: MONOSPACE_FONT, fontWeight: 600 }}>{preview.hash || 'Commit'}</span>
      <span style={lineStyle}>{preview.subject || 'No message'}</span>
    </span>
  );
};

/**
 * Custom node: a colored dot with a label to the right.
 * Edges connect through the dot via top/bottom handles.
 */
const DotNode = memo(({ data }: NodeProps) => {
  const color = (data.color as string) || '#666';
  const label = (data.label as string) || '';
  const icon = data.icon as string | undefined;
  const onSelect = data.onSelect as (() => void) | undefined;
  // Only render the button affordance when there is a handler to call.
  const isClickable = Boolean(data.clickable && onSelect);
  const isSelected = Boolean(data.isSelected);
  const interactiveKind = data.interactiveKind as InteractiveNodeKind | undefined;
  const ariaLabel = (data.ariaLabel as string | undefined) ?? label.replace(/\n/g, ', ');
  const onToggleTurns = data.onToggleTurns as (() => void) | undefined;
  const commentPreview = data.commentPreview as CommentPreview | undefined;
  const commitPreview = data.commitPreview as CommitPreview | undefined;
  const turnCount = (data.turnCount as number | undefined) ?? 0;
  const turnsExpanded = Boolean(data.turnsExpanded);
  const fontSize = (data.fontSize as number) || 12;
  const labelSide = (data.labelSide as string) || 'right';
  const labelMaxWidth = (data.labelMaxWidth as number | undefined) ?? LABEL_MAX_WIDTH;

  const labelPosition = labelSide === 'left' ? { right: DOT_SIZE + 10 } : { left: DOT_SIZE + 10 };

  const hasLabel = label || icon;

  const anchorStyle = {
    position: 'absolute',
    ...labelPosition,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 'max-content',
  } as const;

  const cardStyle = {
    fontSize,
    color: '#1a1a1a',
    whiteSpace: 'pre-wrap',
    width: 'max-content',
    lineHeight: 1.3,
    maxWidth: labelMaxWidth,
    borderRadius: 6,
    padding: '6px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  } as const;

  // Non-interactive cards keep their original look and let clicks fall through to the canvas.
  const staticCardStyle = {
    ...cardStyle,
    cursor: 'default',
    pointerEvents: 'none',
    background: '#fafafa',
    border: `1px solid ${color}`,
  } as const;

  const showTurnToggle = Boolean(onToggleTurns) && turnCount > 0;
  const turnCountLabel = `${turnCount} turn${turnCount !== 1 ? 's' : ''}`;

  const labelContent = commitPreview ? (
    <CommitCardContent preview={commitPreview} />
  ) : (
    <>
      {icon && <img src={icon} alt="" height={16} style={{ flexShrink: 0 }} />}
      {label && <span>{label}</span>}
    </>
  );

  // Mouse clicks are handled by ReactFlow's onNodeClick; keyboard activation
  // has no ReactFlow equivalent, so the label handles Enter/Space itself.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect?.();
    }
  };

  // Comment nodes preview their first comment, and commit nodes their full
  // message, on hover or keyboard focus.
  const tooltipContent = commentPreview ? (
    <CommentTooltipContent preview={commentPreview} />
  ) : commitPreview ? (
    <CommitTooltipContent preview={commitPreview} />
  ) : null;
  const withCommentTooltip = (card: ReactElement) =>
    tooltipContent ? (
      <Tooltip
        label={tooltipContent}
        multiline
        w={COMMENT_TOOLTIP_WIDTH}
        position="right"
        withArrow
        openDelay={250}
        events={{ hover: true, focus: true, touch: false }}
      >
        {card}
      </Tooltip>
    ) : (
      card
    );

  // Stop the click reaching ReactFlow's onNodeClick, which would also open the panel.
  const handleToggleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onToggleTurns?.();
  };

  return (
    <div style={{ width: DOT_SIZE, height: DOT_SIZE, position: 'relative' }}>
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: 'transparent',
          border: 'none',
          top: 0,
          left: DOT_SIZE / 2,
          transform: 'translate(-50%, -50%)',
        }}
      />
      <div
        style={{
          width: DOT_SIZE,
          height: DOT_SIZE,
          borderRadius: '50%',
          background: color,
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: 'transparent',
          border: 'none',
          bottom: 0,
          left: DOT_SIZE / 2,
          transform: 'translate(-50%, 50%)',
        }}
      />
      {/* Leader lines from a turn's time-track segment arrive from the left.
          Declared after the top handle so edges without a targetHandle keep using the top one. */}
      <Handle id="left" type="target" position={Position.Left} style={HIDDEN_HANDLE_STYLE} />
      {hasLabel &&
        (isClickable || showTurnToggle ? (
          <div style={{ ...anchorStyle, pointerEvents: 'none' }}>
            {isClickable ? (
              withCommentTooltip(
                <ClickableLabel
                  $color={color}
                  $selected={isSelected}
                  style={cardStyle}
                  role="button"
                  tabIndex={0}
                  aria-label={ariaLabel}
                  aria-expanded={interactiveKind === InteractiveNodeKind.MoreTurns ? undefined : isSelected}
                  onKeyDown={handleKeyDown}
                >
                  {labelContent}
                  <Icon
                    name={interactiveKind === InteractiveNodeKind.MoreTurns ? 'unfold_more' : 'chevron_right'}
                    size={16}
                    color={color}
                    pointer
                    style={{ flexShrink: 0, marginLeft: 2 }}
                  />
                </ClickableLabel>,
              )
            ) : (
              <div style={{ ...staticCardStyle, position: 'relative' }}>{labelContent}</div>
            )}
            {showTurnToggle && (
              <TurnToggle
                type="button"
                className="nodrag nopan"
                $color={color}
                aria-expanded={turnsExpanded}
                // The accessible name must contain the visible text (WCAG 2.5.3).
                aria-label={turnsExpanded ? 'Hide turns' : `Show ${turnCountLabel}`}
                onClick={handleToggleClick}
              >
                <Icon name={turnsExpanded ? 'expand_less' : 'expand_more'} size={12} color={color} pointer />
                {turnsExpanded ? 'Hide turns' : turnCountLabel}
              </TurnToggle>
            )}
          </div>
        ) : commitPreview ? (
          // Not clickable, but it takes pointer and keyboard focus so its preview can open.
          <div style={{ ...anchorStyle, pointerEvents: 'none' }}>
            {withCommentTooltip(
              <FocusableCard
                style={{ ...staticCardStyle, pointerEvents: 'auto' }}
                role="group"
                tabIndex={0}
                aria-label={ariaLabel}
              >
                {labelContent}
              </FocusableCard>,
            )}
          </div>
        ) : (
          <div style={{ ...anchorStyle, ...staticCardStyle }}>{labelContent}</div>
        ))}
    </div>
  );
});

DotNode.displayName = 'DotNode';

/**
 * The rail of an expanded session's time track, hung from the session dot.
 */
const TurnTrackNode = memo(({ data }: NodeProps) => (
  <div
    style={{
      width: data.width as number,
      height: data.height as number,
      background: data.color as string,
      opacity: 0.25,
      borderRadius: 1,
    }}
  >
    <Handle type="target" position={Position.Top} style={HIDDEN_HANDLE_STYLE} />
    {/* The session lane's edge to the next session leaves from here, below the whole track. */}
    <Handle type="source" position={Position.Bottom} style={HIDDEN_HANDLE_STYLE} />
  </div>
));

TurnTrackNode.displayName = 'TurnTrackNode';

/**
 * A step marker on an expanded session's track: a filled square per turn, or a
 * hollow one for the "+N more" card. A turn marker is a mouse shortcut to open
 * the turn (the card stays the keyboard target). Its leader line to the card
 * leaves from the right.
 */
const TurnMarkerNode = memo(({ data }: NodeProps) => {
  const isSelected = Boolean(data.isSelected);
  const isOpenable = Boolean(data.onSelect);
  const color = data.color as string;
  return (
    <div
      title={data.title as string | undefined}
      style={{
        width: data.size as number,
        height: data.size as number,
        boxSizing: 'border-box',
        background: data.hollow ? 'var(--base-white)' : color,
        border: `1.5px solid ${color}`,
        borderRadius: 2,
        cursor: isOpenable ? 'pointer' : 'default',
        boxShadow: isSelected ? '0 0 0 2px var(--secondary-base)' : 'none',
      }}
    >
      <Handle id="right" type="source" position={Position.Right} style={HIDDEN_HANDLE_STYLE} />
    </div>
  );
});

TurnMarkerNode.displayName = 'TurnMarkerNode';

/**
 * The real time between two consecutive track steps, e.g. "10s" or, for long
 * pauses, "⋯ 2d 4h ⋯".
 */
const TrackGapLabelNode = memo(({ data }: NodeProps) => (
  <div
    style={{
      font: `${data.isLong ? 600 : 500} 10px/14px 'Figtree', sans-serif`,
      color: data.color as string,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
    }}
  >
    {data.isLong ? `\u22ef ${data.label as string} \u22ef` : (data.label as string)}
  </div>
));

TrackGapLabelNode.displayName = 'TrackGapLabelNode';

const NODE_TYPES = {
  dotNode: DotNode,
  turnTrack: TurnTrackNode,
  turnMarker: TurnMarkerNode,
  trackGapLabel: TrackGapLabelNode,
};

/**
 * Renders the unified process graph as a timeline of ReactFlow dot nodes.
 *
 * Responsibilities:
 * 1. Lay out the graph, including the turns of any session the user expands
 * 2. Hide legend-toggled lane groups, reconnecting edges around them
 * 3. Wire session, turn and "+N more" nodes to their selection / expansion actions
 * 4. Expand or collapse every session from one toggle in the canvas controls
 *
 * @param {UnifiedProcessGraphChartProps} props - Graph data plus optional session and turn selection handlers
 * @returns {JSX.Element} The graph canvas with its lane legend
 */
export const UnifiedProcessGraphChart = ({
  data,
  onSelectSession,
  onSelectTurn,
  selectedSessionId = null,
  selectedTurnId = null,
  onSelectComment,
  selectedCommentId = null,
}: UnifiedProcessGraphChartProps) => {
  const [hiddenGroups, setHiddenGroups] = useState<Set<string>>(new Set());
  const [legendOpen, setLegendOpen] = useState(false);
  // Keyed by session node id; a session absent from the map is collapsed.
  const [turnExpansion, setTurnExpansion] = useState<ReadonlyMap<string, TurnExpansion>>(new Map());

  const toggleTurns = useCallback((sessionNodeId: string) => {
    setTurnExpansion((previous) => {
      const next = new Map(previous);
      if (next.has(sessionNodeId)) {
        next.delete(sessionNodeId);
      } else {
        next.set(sessionNodeId, TurnExpansion.Preview);
      }
      return next;
    });
  }, []);

  const collapseAllTurns = useCallback(() => {
    setTurnExpansion((previous) => (previous.size === 0 ? previous : new Map()));
  }, []);

  // Sessions that render a turns toggle: drawn on the graph and with turns to show.
  const expandableSessionNodeIds = useMemo(
    () => data.agent_session_nodes.filter((node) => node.timestamp && node.turns.length > 0).map((node) => node.id),
    [data],
  );
  const canExpandAll = expandableSessionNodeIds.some((id) => !turnExpansion.has(id));
  // One button toggles every session: it collapses while any session is open, otherwise it expands.
  const anyExpanded = turnExpansion.size > 0;
  const bulkToggleLabel = anyExpanded ? 'Collapse all sessions' : 'Expand all sessions';

  // Opens every collapsed session to its preview; sessions already showing all turns stay as they are.
  const expandAllTurns = useCallback(() => {
    setTurnExpansion((previous) => {
      const next = new Map(previous);
      expandableSessionNodeIds.forEach((id) => {
        if (!next.has(id)) next.set(id, TurnExpansion.Preview);
      });
      return next.size === previous.size ? previous : next;
    });
  }, [expandableSessionNodeIds]);

  const showAllTurns = useCallback((sessionNodeId: string) => {
    setTurnExpansion((previous) => new Map(previous).set(sessionNodeId, TurnExpansion.All));
  }, []);

  // A selected turn (e.g. from a `?turn=` link) must be visible, so expand its
  // session far enough to show it. Tracked by turn id so it fires once per
  // selection: a graph refetch must not re-expand a session the user collapsed.
  const autoExpandedTurnRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedTurnId || autoExpandedTurnRef.current === selectedTurnId) return;
    // Only look inside the selected session, matching what the turn panel shows.
    const session = data.agent_session_nodes.find(
      (node) =>
        (!selectedSessionId || toAgentSessionId(node.id) === selectedSessionId) &&
        node.turns.some((turn) => turn.id === selectedTurnId),
    );
    if (!session) return;
    autoExpandedTurnRef.current = selectedTurnId;
    const turnIndex = [...session.turns]
      .sort((a, b) => a.turn_number - b.turn_number)
      .findIndex((turn) => turn.id === selectedTurnId);
    const needed = turnIndex >= TURN_PREVIEW_LIMIT ? TurnExpansion.All : TurnExpansion.Preview;
    setTurnExpansion((previous) => {
      const current = previous.get(session.id);
      if (current === TurnExpansion.All || current === needed) return previous;
      return new Map(previous).set(session.id, needed);
    });
  }, [data, selectedSessionId, selectedTurnId]);

  const toggleGroup = useCallback((groupKey: string) => {
    setHiddenGroups((previous) => {
      const next = new Set(previous);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  const allGraph = useMemo(() => buildFlowGraph(data, turnExpansion), [data, turnExpansion]);

  const { nodes: visibleNodes, edges } = useMemo(() => {
    if (hiddenGroups.size === 0) return allGraph;

    const hiddenRanges: [number, number][] = [];
    LANE_GROUPS.forEach((group) => {
      if (hiddenGroups.has(group.key)) {
        hiddenRanges.push(group.xRange as [number, number]);
      }
    });

    const isHidden = (x: number) => hiddenRanges.some(([min, max]) => x >= min && x <= max);
    const filteredNodes = allGraph.nodes.filter((node) => !isHidden(node.position.x));
    const visibleIds = new Set(filteredNodes.map((node) => node.id));

    // Build adjacency maps to trace through hidden nodes
    const forwardAdj = new Map<string, string[]>();
    const reverseAdj = new Map<string, string[]>();
    for (const edge of allGraph.edges) {
      forwardAdj.set(edge.source, [...(forwardAdj.get(edge.source) || []), edge.target]);
      reverseAdj.set(edge.target, [...(reverseAdj.get(edge.target) || []), edge.source]);
    }

    // Walk through hidden nodes to find nearest visible node
    const findVisible = (startId: string, adj: Map<string, string[]>, visited: Set<string>): string | null => {
      if (visibleIds.has(startId)) return startId;
      if (visited.has(startId)) return null;
      visited.add(startId);
      for (const neighbor of adj.get(startId) || []) {
        const result = findVisible(neighbor, adj, visited);
        if (result) return result;
      }
      return null;
    };

    const reconnectedEdges: Edge[] = [];
    const seenEdgeKeys = new Set<string>();

    for (const edge of allGraph.edges) {
      const sourceVisible = visibleIds.has(edge.source);
      const targetVisible = visibleIds.has(edge.target);

      if (sourceVisible && targetVisible) {
        reconnectedEdges.push(edge);
        continue;
      }

      const resolvedSource = sourceVisible ? edge.source : findVisible(edge.source, reverseAdj, new Set());
      const resolvedTarget = targetVisible ? edge.target : findVisible(edge.target, forwardAdj, new Set());

      if (resolvedSource && resolvedTarget && resolvedSource !== resolvedTarget) {
        const key = `${resolvedSource}->${resolvedTarget}`;
        if (!seenEdgeKeys.has(key)) {
          seenEdgeKeys.add(key);
          reconnectedEdges.push({
            ...edge,
            id: `reconnected-${key}`,
            source: resolvedSource,
            target: resolvedTarget,
          });
        }
      }
    }

    return { nodes: filteredNodes, edges: reconnectedEdges };
  }, [allGraph, hiddenGroups]);

  // Selection and actions are layered on after filtering so selecting a node
  // never rebuilds the layout.
  const nodes = useMemo(
    () =>
      visibleNodes.map((node) => {
        if (node.type === 'turnMarker') {
          const markerTurnId = node.data.markerTurnId as string | undefined;
          const markerSessionId = node.data.sessionId as string | undefined;
          if (!markerTurnId || !markerSessionId) return node;
          return {
            ...node,
            data: {
              ...node.data,
              isSelected: markerTurnId === selectedTurnId,
              onSelect: onSelectTurn ? () => onSelectTurn(markerSessionId, markerTurnId) : undefined,
            },
          };
        }
        if (!node.data.clickable) return node;
        const sessionId = node.data.sessionId as string;
        const sessionNodeId = node.data.sessionNodeId as string;
        const turnId = node.data.turnId as string | undefined;
        const label = ((node.data.label as string) || '').replace(/\n/g, ', ');

        switch (node.data.interactiveKind) {
          case InteractiveNodeKind.Session:
            return {
              ...node,
              data: {
                ...node.data,
                isSelected: sessionId === selectedSessionId,
                ariaLabel: `Open agent session details: ${label}`,
                onSelect: onSelectSession ? () => onSelectSession(sessionId) : undefined,
                onToggleTurns: () => toggleTurns(sessionNodeId),
              },
            };
          case InteractiveNodeKind.Turn:
            return {
              ...node,
              data: {
                ...node.data,
                isSelected: !!turnId && turnId === selectedTurnId,
                ariaLabel: `Open turn details: ${label}`,
                onSelect: onSelectTurn && turnId ? () => onSelectTurn(sessionId, turnId) : undefined,
              },
            };
          case InteractiveNodeKind.Comment: {
            const commentId = node.data.commentId as string;
            return {
              ...node,
              data: {
                ...node.data,
                isSelected: commentId === selectedCommentId,
                ariaLabel: `Open review comments: ${label}`,
                onSelect: onSelectComment ? () => onSelectComment(commentId) : undefined,
              },
            };
          }
          case InteractiveNodeKind.MoreTurns:
            return {
              ...node,
              data: {
                ...node.data,
                ariaLabel: `Show all turns: ${label}`,
                onSelect: () => showAllTurns(sessionNodeId),
              },
            };
          default:
            return node;
        }
      }),
    [
      visibleNodes,
      selectedSessionId,
      selectedTurnId,
      selectedCommentId,
      onSelectSession,
      onSelectTurn,
      onSelectComment,
      toggleTurns,
      showAllTurns,
    ],
  );

  // Mouse clicks run the same action the label's keyboard handler does; turn
  // markers on a track share their card's action as a mouse-only shortcut.
  const handleNodeClick: NodeMouseHandler<Node> = useCallback((_event, node) => {
    const onSelect = node.data.onSelect as (() => void) | undefined;
    if (node.data.clickable || node.type === 'turnMarker') onSelect?.();
  }, []);

  return (
    <ReactFlowProvider>
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={NODE_TYPES}
          onNodeClick={handleNodeClick}
          fitView
          nodesDraggable={false}
          nodesConnectable={false}
          panOnDrag
          zoomOnScroll
          proOptions={{ hideAttribution: true }}
        >
          <Background />
          <Controls position="bottom-right" showInteractive={false}>
            <Tooltip label={bulkToggleLabel} position="left" withArrow openDelay={250}>
              <ControlButton
                onClick={anyExpanded ? collapseAllTurns : expandAllTurns}
                disabled={!anyExpanded && !canExpandAll}
                aria-label={bulkToggleLabel}
              >
                <Icon
                  name={anyExpanded ? 'unfold_less' : 'unfold_more'}
                  size={16}
                  pointer={anyExpanded || canExpandAll}
                />
              </ControlButton>
            </Tooltip>
          </Controls>
        </ReactFlow>
        <div
          style={{
            position: 'absolute',
            bottom: 15,
            left: 2,
            background: 'white',
            borderRadius: 8,
            padding: 12,
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            zIndex: 5,
            maxWidth: 200,
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={() => setLegendOpen((previous) => !previous)}
          >
            <span style={{ fontSize: 13, fontWeight: 600 }}>Legend</span>
            <span style={{ fontSize: 18, lineHeight: 1 }}>{legendOpen ? '\u25BE' : '\u25B8'}</span>
          </div>
          <div
            style={{
              maxHeight: legendOpen ? 300 : 0,
              opacity: legendOpen ? 1 : 0,
              overflow: 'hidden',
              transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out',
            }}
          >
            <div
              style={{
                marginTop: 8,
                borderTop: '1px solid #e0e0e0',
                paddingTop: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              {LANE_GROUPS.map((group) => (
                <label
                  key={group.key}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12 }}
                >
                  <input
                    type="checkbox"
                    checked={!hiddenGroups.has(group.key)}
                    onChange={() => toggleGroup(group.key)}
                    style={{ accentColor: group.color, margin: 0 }}
                  />
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: group.color,
                      flexShrink: 0,
                    }}
                  />
                  <span>{group.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </ReactFlowProvider>
  );
};
